import type { ScopeNode } from "@/types/bbel-types";

/** The value part of a leaf's hint (`string · "ACME"` → `"ACME"`); descriptions pass through as-is. */
export function sampleFromHint(hint: string | undefined): string {
  if (!hint) return "";
  const dot = hint.indexOf("·");
  return dot >= 0 ? hint.slice(dot + 1).trim() : hint;
}

/**
 * Prune the scope tree to search matches, keeping hierarchy: a node stays when it matches, has a
 * matching descendant (pruned path down to it), or descends from a matching node (a branch hit
 * keeps its whole subtree browsable). A node matches when its label contains the query, or when
 * every whitespace-separated term appears somewhere in its full path — so "report status" reaches
 * `services.GetReport.statusCode`. An empty term returns the tree unchanged.
 */
export function filterScopeTree(nodes: ScopeNode[], term: string): ScopeNode[] {
  const q = term.trim().toLowerCase();
  if (!q) return nodes;
  const terms = q.split(/\s+/);

  const matches = (node: ScopeNode) =>
    node.label.toLowerCase().includes(q) ||
    terms.every((t) => node.path.toLowerCase().includes(t));

  const walk = (node: ScopeNode): ScopeNode | null => {
    if (matches(node)) return node;
    const kids = (node.children ?? [])
      .map(walk)
      .filter((n): n is ScopeNode => n !== null);
    return kids.length ? { ...node, children: kids } : null;
  };

  return nodes.map(walk).filter((n): n is ScopeNode => n !== null);
}

/**
 * Plan a palette insertion. `bare` is the token without slot braces ("services.output",
 * "jsonDecode()"); `cursorBackBy` is how many chars from the end of `bare` the caret should land
 * (1 for a function so it sits inside the parens, 0 for a plain path). When the caret is not already
 * inside a `{{ … }}` slot the token is wrapped and the caret offset also accounts for the trailing
 * " }}". Returns the text to insert and where the caret sits, counted back from the text's end.
 */
export function planInsert(
  bare: string,
  cursorBackBy: number,
  inSlot: boolean,
): { text: string; backFromEnd: number } {
  if (inSlot) return { text: bare, backFromEnd: cursorBackBy };
  return { text: `{{ ${bare} }}`, backFromEnd: cursorBackBy + 3 }; // trailing " }}" = 3 chars
}
